import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/Button';
import { PackageIcon, CalendarIcon, SparklesIcon, TrendingUpIcon, MessageSquareIcon, BarChartIcon, ZapIcon, ShieldCheckIcon, ArrowRightIcon, ChevronDownIcon, CheckCircleIcon, UsersIcon, HandshakeIcon } from 'lucide-react';
export function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const features = [{
    icon: <SparklesIcon className="h-6 w-6" data-id="element-2559" />,
    title: 'AI-Powered Matching',
    description: 'Our algorithms analyze 50+ data points to find your perfect partners.'
  }, {
    icon: <ZapIcon className="h-6 w-6" data-id="element-2560" />,
    title: 'Quick Setup',
    description: 'Create your profile in under 5 minutes and start matching immediately.'
  }, {
    icon: <MessageSquareIcon className="h-6 w-6" data-id="element-2561" />,
    title: 'Direct Communication',
    description: 'Message partners directly through our secure platform.'
  }, {
    icon: <BarChartIcon className="h-6 w-6" data-id="element-2562" />,
    title: 'Performance Analytics',
    description: 'Track your sponsorship ROI with detailed insights and reporting.'
  }, {
    icon: <ShieldCheckIcon className="h-6 w-6" data-id="element-2563" />,
    title: 'Verified Partners',
    description: 'All brands and organizers are verified for quality assurance.'
  }, {
    icon: <TrendingUpIcon className="h-6 w-6" data-id="element-2564" />,
    title: 'Growth Focused',
    description: 'Tools designed to help you scale your sponsorship strategy.'
  }];
  const stats = [{
    value: '500+',
    label: 'Active Brands'
  }, {
    value: '1,000+',
    label: 'Events Listed'
  }, {
    value: '95%',
    label: 'Match Rate'
  }, {
    value: '48h',
    label: 'Avg. Response Time'
  }];
  const faqs = [{
    question: 'How does the matching algorithm work?',
    answer: 'Our AI analyzes your profile, target audience, budget, and goals to find partners with complementary needs. We consider factors like industry alignment, audience demographics, event type, and sponsorship preferences to ensure high-quality matches.'
  }, {
    question: 'Is SponsrAI free to use?',
    answer: 'Creating a profile and browsing matches is completely free. We only charge a small success fee when you finalize a partnership through our platform, ensuring our incentives are aligned with your success.'
  }, {
    question: 'How long does it take to get started?',
    answer: 'Most users complete their profile in under 5 minutes. Once your profile is live, you can start receiving match suggestions immediately. Many users connect with their first potential partner within 24 hours.'
  }, {
    question: 'What types of events are supported?',
    answer: "We support all types of events including conferences, festivals, sports events, community gatherings, workshops, networking events, and more. Whether you're organizing a 50-person workshop or a 10,000-person festival, we can help you find the right sponsors."
  }, {
    question: 'How do I know if a match is right for me?',
    answer: "Each match comes with a compatibility score and detailed breakdown showing why we think you'd work well together. You can review audience overlap, budget alignment, and shared goals before reaching out."
  }];
  return <div className="min-h-screen bg-white" data-id="element-2565">
      <Navbar data-id="element-2566" />

      {/* Hero Section */}
      <section className="relative overflow-hidden" data-id="element-2567">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50" data-id="element-2568" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iLjAyIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0tNiA2aC0ydi00aDJ2NHptMC02aC0ydi00aDJ2NHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50" data-id="element-2569" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-32" data-id="element-2570">
          <div className="text-center max-w-4xl mx-auto" data-id="element-2571">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6" data-id="element-2572">
              <SparklesIcon className="h-4 w-4 mr-2" data-id="element-2573" />
              AI-Powered Sponsorship Matching
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6" data-id="element-2574">
              Connect Brands with
              <span className="text-blue-600" data-id="element-2575"> Perfect Events</span>
            </h1>

            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-2576">
              SponsrAI uses intelligent matching to connect brands seeking
              exposure with event organizers looking for sponsors. Find your
              perfect partnership in minutes.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2577">
              <Link to="/register" data-id="element-2578">
                <Button size="lg" rightIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-2580" />} data-id="element-2579">
                  Get Started Free
                </Button>
              </Link>
              <a href="#features" data-id="element-2581">
                <Button variant="outline" size="lg" data-id="element-2582">
                  See How It Works
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-900" data-id="element-2583">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2584">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-2585">
            {stats.map((stat, index) => <div key={index} className="text-center" data-id="element-2586">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1" data-id="element-2587">
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm" data-id="element-2588">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Choose Your Path Section */}
      <section className="py-20 bg-white" data-id="element-2589">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2590">
          <div className="text-center mb-12" data-id="element-2591">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2592">
              Choose Your Path
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2593">
              Whether you're a brand looking for exposure or an organizer
              seeking sponsors, we've got you covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-2594">
            {/* Brand Card */}
            <Link to="/onboarding/brand" className="group relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-transparent hover:border-blue-300 transition-all hover:shadow-xl" data-id="element-2595">
              <div className="absolute top-6 right-6 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-2596">
                For Brands
              </div>
              <div className="bg-blue-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-2597">
                <PackageIcon className="h-7 w-7 text-white" data-id="element-2598" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2599">
                I'm a Brand
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2600">
                Find the perfect events to showcase your products, reach new
                audiences, and build meaningful partnerships.
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2601">
                <li className="flex items-center text-sm text-gray-700" data-id="element-2602">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-2603" />
                  AI-matched event recommendations
                </li>
                <li className="flex items-center text-sm text-gray-700" data-id="element-2604">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-2605" />
                  Direct organizer communication
                </li>
                <li className="flex items-center text-sm text-gray-700" data-id="element-2606">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-2607" />
                  Campaign performance tracking
                </li>
              </ul>
              <div className="flex items-center text-blue-600 font-semibold group-hover:translate-x-2 transition-transform" data-id="element-2608">
                Get Started <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-2609" />
              </div>
            </Link>

            {/* Organizer Card */}
            <Link to="/onboarding/organizer" className="group relative bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 border-2 border-transparent hover:border-purple-300 transition-all hover:shadow-xl" data-id="element-2610">
              <div className="absolute top-6 right-6 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-2611">
                For Organizers
              </div>
              <div className="bg-purple-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-2612">
                <CalendarIcon className="h-7 w-7 text-white" data-id="element-2613" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2614">
                I'm an Organizer
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2615">
                Connect with brands that align with your event's audience and
                values. Secure sponsorships effortlessly.
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2616">
                <li className="flex items-center text-sm text-gray-700" data-id="element-2617">
                  <CheckCircleIcon className="h-5 w-5 text-purple-600 mr-2" data-id="element-2618" />
                  Matched brand suggestions
                </li>
                <li className="flex items-center text-sm text-gray-700" data-id="element-2619">
                  <CheckCircleIcon className="h-5 w-5 text-purple-600 mr-2" data-id="element-2620" />
                  Sponsorship proposal tools
                </li>
                <li className="flex items-center text-sm text-gray-700" data-id="element-2621">
                  <CheckCircleIcon className="h-5 w-5 text-purple-600 mr-2" data-id="element-2622" />
                  Event analytics dashboard
                </li>
              </ul>
              <div className="flex items-center text-purple-600 font-semibold group-hover:translate-x-2 transition-transform" data-id="element-2623">
                Get Started <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-2624" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50" data-id="element-2625">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2626">
          <div className="text-center mb-16" data-id="element-2627">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2628">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2629">
              Powerful tools and features designed to make sponsorship matching
              simple and effective.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2630">
            {features.map((feature, index) => <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all" data-id="element-2631">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-2632">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2633">
                  {feature.title}
                </h3>
                <p className="text-gray-600" data-id="element-2634">{feature.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white" data-id="element-2635">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2636">
          <div className="text-center mb-16" data-id="element-2637">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2638">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2639">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto" data-id="element-2640">
            {[{
            step: '1',
            title: 'Create Your Profile',
            description: "Tell us about your brand or event, your goals, and what you're looking for in a partner.",
            icon: <UsersIcon className="h-6 w-6" data-id="element-2641" />
          }, {
            step: '2',
            title: 'Get Matched',
            description: 'Our AI analyzes your profile and suggests the most compatible partners based on 50+ factors.',
            icon: <SparklesIcon className="h-6 w-6" data-id="element-2642" />
          }, {
            step: '3',
            title: 'Connect & Collaborate',
            description: 'Reach out to your matches, discuss opportunities, and build successful partnerships.',
            icon: <HandshakeIcon className="h-6 w-6" data-id="element-2643" />
          }].map((item, index) => <div key={index} className="text-center" data-id="element-2644">
                <div className="relative inline-flex mb-6" data-id="element-2645">
                  <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center text-white" data-id="element-2646">
                    {item.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 bg-gray-900 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-2647">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2648">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-2649">{item.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50" data-id="element-2650">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2651">
          <div className="text-center mb-12" data-id="element-2652">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2653">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2654">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-4" data-id="element-2655">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2656">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-2657">
                  <span className="font-medium text-gray-900" data-id="element-2658">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-2659" />
                </button>
                {openFaq === index && <div className="px-6 pb-4" data-id="element-2660">
                    <p className="text-gray-600" data-id="element-2661">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600" data-id="element-2662">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2663">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2664">
            Ready to Find Your Perfect Match?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto" data-id="element-2665">
            Join hundreds of brands and organizers already using SponsrAI to
            build successful partnerships.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2666">
            <Link to="/register" data-id="element-2667">
              <Button variant="outline" size="lg" className="bg-white text-blue-600 border-white hover:bg-blue-50" data-id="element-2668">
                Create Free Account
              </Button>
            </Link>
            <Link to="/login" data-id="element-2669">
              <Button variant="ghost" size="lg" className="text-white border-white/30 hover:bg-white/10" data-id="element-2670">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12" data-id="element-2671">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2672">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-2673">
            <div className="flex items-center mb-4 md:mb-0" data-id="element-2674">
              <div className="bg-blue-600 p-2 rounded-lg mr-3" data-id="element-2675">
                <HandshakeIcon className="h-5 w-5 text-white" data-id="element-2676" />
              </div>
              <span className="text-white font-semibold text-lg" data-id="element-2677">SponsrAI</span>
            </div>
            <div className="flex space-x-8 text-sm" data-id="element-2678">
              <a href="/about" className="hover:text-white transition-colors" data-id="element-2679">
                About
              </a>
              <a href="/privacy" className="hover:text-white transition-colors" data-id="element-2680">
                Privacy
              </a>
              <a href="/terms" className="hover:text-white transition-colors" data-id="element-2681">
                Terms
              </a>
              <a href="/contact" className="hover:text-white transition-colors" data-id="element-2682">
                Contact
              </a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm" data-id="element-2683">
            © {new Date().getFullYear()} SponsrAI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}